/**
 * 생성기 입력 초안 — 주제 · 탭별 링크 칸 글 · 탭별 붙인 자료 · 조건. 계측·토스트·안내를 모르는 순수 입력 상태다.
 * 실행 결과 반응이 「지금 입력이 그 실행을 만든 초안 그대로인가」를 묻는 판정도 여기 둔다.
 */
import { useMemo, useRef, useState } from 'react';

import {
  AI_GENERATE_DOCK_DEFAULT_COUNT,
  AI_GENERATE_DOCK_DEFAULT_DIFFICULTY,
  AI_GENERATE_DOCK_DEFAULT_QUIZ_TYPES,
  type AiGenerateDockSourceType,
} from '../consts/aiGenerateDock.const';
import {
  type AiGenerateDockAttachment,
  type AiGenerateDockConditions,
} from '../types/aiGenerateDock.types';
import {
  type AiGenerateDockAttachedSource,
  listAiGenerateDockAttachedSources,
} from '../utils/aiGenerateDockPreregistration';
import {
  type AiGenerateDockRunInput,
  type AiGenerateDockSource,
} from '../utils/aiGenerateDockRunInput';

const DEFAULT_CONDITIONS: AiGenerateDockConditions = {
  count: AI_GENERATE_DOCK_DEFAULT_COUNT,
  quizTypes: AI_GENERATE_DOCK_DEFAULT_QUIZ_TYPES,
  difficulty: AI_GENERATE_DOCK_DEFAULT_DIFFICULTY,
};

export type AiGenerateDockDraftSource = {
  attachment: AiGenerateDockAttachment;
  source: AiGenerateDockSource;
};

/** 탭별 값 — 자료는 탭마다 다르다. 다른 탭에 갔다 와도 그 탭에 붙인 자료가 남는다. */
type ByTab<T> = Partial<Record<AiGenerateDockSourceType, T>>;

/** 탭별 아직 붙이지 않은 링크 칸 글이 같은가 — 빈 칸과 칸 없음은 같다. */
const isSameLinkDrafts = (left: ByTab<string>, right: ByTab<string>) => {
  const tabs = new Set([
    ...Object.keys(left),
    ...Object.keys(right),
  ]) as Set<AiGenerateDockSourceType>;
  return [...tabs].every(tab => (left[tab] ?? '') === (right[tab] ?? ''));
};

/** 탭별로 붙인 자료가 그대로인가 — 같은 첨부(source 객체)여야 같다. 비어 있는 탭끼리는 같다. */
const isSameTabSources = (
  current: ByTab<AiGenerateDockDraftSource>,
  atRun: ByTab<AiGenerateDockSource>,
) => {
  const tabs = new Set([
    ...Object.keys(current),
    ...Object.keys(atRun),
  ]) as Set<AiGenerateDockSourceType>;
  return [...tabs].every(tab => current[tab]?.source === atRun[tab]);
};

/** 제출 순간의 초안 중 실행 입력에 실리지 않는 것 — 링크 칸 글과 다른 탭에 붙어 있던 자료. */
type RunDraftSnapshot = {
  linkUrls: ByTab<string>;
  sources: ByTab<AiGenerateDockSource>;
};

export type AiGenerateDockDraft = {
  topic: string;
  setTopic: (topic: string) => void;
  /** 지금 탭의 아직 붙이지 않은 링크 칸 글. */
  linkUrl: string;
  setLinkUrl: (url: string) => void;
  conditions: AiGenerateDockConditions;
  setConditions: (conditions: AiGenerateDockConditions) => void;
  /** 지금 탭에 붙인 자료. */
  dockSource: AiGenerateDockDraftSource | undefined;
  /** 모든 탭에 붙어 있는 자료 — 사전 등록 대상. */
  attachedSources: AiGenerateDockAttachedSource[];
  setTabSource: (
    tab: AiGenerateDockSourceType,
    next: AiGenerateDockDraftSource | undefined,
  ) => void;
  /** 지금 탭의 자료와 링크 칸 글을 비운다. */
  clearSource: () => void;
  /** 제출 순간의 링크 칸 글과 탭별 자료를 그 실행에 묶는다. */
  markRun: (source: AiGenerateDockSource) => void;
  /** 지금 입력칸·자료가 그 실행을 만든 초안 그대로인가 — 실행 뒤 새로 쓰거나 어느 탭에든 붙였으면 아니다. */
  isDraftOf: (input: AiGenerateDockRunInput) => boolean;
  /** 결과가 목록에 들어갔다 — 주제·자료·링크 칸을 모두 비운다(조건은 남긴다). */
  clear: () => void;
};

export const useAiGenerateDockDraft = (
  sourceType: AiGenerateDockSourceType,
): AiGenerateDockDraft => {
  const [topic, setTopic] = useState('');
  const [linkUrls, setLinkUrls] = useState<ByTab<string>>({});
  const [dockSources, setDockSources] = useState<
    ByTab<AiGenerateDockDraftSource>
  >({});
  const [conditions, setConditions] = useState(DEFAULT_CONDITIONS);
  /**
   * 실행마다 제출 순간의 링크 칸 글·탭별 자료 — 실행 입력의 source 객체가 키다(다시 시도·자료 등록 뒤 입력도 같은 source 를
   * 그대로 들고 다닌다). 실행 뒤 링크만 새로 쓰거나 다른 탭에만 새로 붙여도 그 실행의 초안이 아니게 판정하려고 둔다.
   */
  const draftsAtRunRef = useRef(
    new WeakMap<AiGenerateDockRunInput['source'], RunDraftSnapshot>(),
  );

  const dockSource = dockSources[sourceType];

  // 붙어 있는 첨부는 곧바로 서버에 등록해 둔다 — 첨부가 바뀌거나 사라지면 등록도 따라 끊긴다.
  const attachedSources = useMemo(
    () =>
      listAiGenerateDockAttachedSources({
        file: dockSources.file?.source,
        youtube: dockSources.youtube?.source,
        website: dockSources.website?.source,
      }),
    [dockSources],
  );

  const setTabSource = (
    tab: AiGenerateDockSourceType,
    next: AiGenerateDockDraftSource | undefined,
  ) => setDockSources(prev => ({ ...prev, [tab]: next }));

  return {
    topic,
    setTopic,
    linkUrl: linkUrls[sourceType] ?? '',
    setLinkUrl: url => setLinkUrls(prev => ({ ...prev, [sourceType]: url })),
    conditions,
    setConditions,
    dockSource,
    attachedSources,
    setTabSource,
    clearSource: () => {
      setTabSource(sourceType, undefined);
      setLinkUrls(prev => ({ ...prev, [sourceType]: '' }));
    },
    markRun: source => {
      draftsAtRunRef.current.set(source, {
        linkUrls,
        sources: Object.fromEntries(
          Object.entries(dockSources).map(([tab, draft]) => [
            tab,
            draft?.source,
          ]),
        ),
      });
    },
    isDraftOf: input => {
      if (topic !== input.topic) return false;
      const atRun = draftsAtRunRef.current.get(input.source);
      // 제출 순간을 묶지 않은 실행이면 지금 탭 자료만 견준다.
      if (!atRun) {
        return input.source.kind === 'none'
          ? !dockSource
          : dockSource?.source === input.source;
      }
      return (
        isSameTabSources(dockSources, atRun.sources) &&
        isSameLinkDrafts(linkUrls, atRun.linkUrls)
      );
    },
    clear: () => {
      setTopic('');
      setDockSources({});
      setLinkUrls({});
    },
  };
};
